import { IconLifebuoy, IconMessageCircle, IconPhone, IconPhoneCall, IconSparkles, IconX } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, URGENCIES, type EmergencyCase, type HelpResult, type TripStatus, type Urgency } from "../api";
import { telLink } from "./Footer";
import { relativeTime } from "../format";
import { URGENCY_KEYS, type Key } from "../i18n";
import { useSession } from "../session";
import { ErrorNote, StatusChip } from "./ui";

const URGENCY_HINTS: Record<Urgency, Key> = { urgent: "urgencyUrgentHint", soon: "urgencySoonHint", info: "urgencyInfoHint" };

/** Help now is live from confirmation until two days after the trip ends (docs/16 §2.8, decision 16). */
export function helpAvailable(status: TripStatus, endsOn: string | null): boolean {
  if (status === "confirmed" || status === "in_progress") return true;
  if (status !== "completed" || !endsOn) return false;
  const [y, m, d] = endsOn.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return false;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return (today - Date.UTC(y, m - 1, d)) / 86_400_000 <= 2;
}

/** The "Help now" button of the help card with the trip's previous cases under it; the sheet opens the form. */
export function HelpNow({ tripId, available, open, onOpen, onClose }: { tripId: string; available: boolean; open: boolean; onOpen: () => void; onClose: () => void }) {
  const { lang, t } = useSession();
  const [cases, setCases] = useState<EmergencyCase[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!available) return;
    api
      .helpCases(tripId)
      .then((res) => setCases([...res.cases].sort((a, b) => b.openedAt.localeCompare(a.openedAt))))
      .catch(() => setCases([]));
  }, [tripId, available, tick]);

  return (
    <>
      <button type="button" className="button button-primary button-block" disabled={!available} title={available ? undefined : t("helpNowSoon")} onClick={onOpen}>
        <IconLifebuoy size={18} /> {t("helpNow")}
      </button>
      {!available && <p className="muted small">{t("helpNowSoon")}</p>}
      {cases.length > 0 && (
        <div className="cases">
          <p className="notices-subtitle">{t("previousCases")}</p>
          <ul className="list">
            {cases.map((c) => (
              <li key={c.id} className="list-row case-row">
                <span className="list-text">
                  <strong>{c.what}</strong>
                  <span className="muted small">
                    {t("openedAgo", { when: relativeTime(c.openedAt, lang) })}
                    {c.assignedAgentName ? ` · ${c.assignedAgentName}` : ""}
                  </span>
                </span>
                <span className={`chip chip-urgency-${c.urgency}`}>{URGENCY_KEYS[c.urgency] ? t(URGENCY_KEYS[c.urgency]!) : c.urgency}</span>
                <StatusChip status={c.status} lang={lang} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {open && (
        <HelpSheet
          tripId={tripId}
          onClose={() => {
            onClose();
            setTick((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

/** The fixed bar during the trip (docs/16 §2.2: always visible). */
export function HelpBar({ onOpen }: { onOpen: () => void }) {
  const { t } = useSession();
  return (
    <div className="help-bar">
      <button type="button" className="button button-primary" onClick={onOpen}>
        <IconLifebuoy size={18} /> {t("helpNow")}
      </button>
    </div>
  );
}

function HelpSheet({ tripId, onClose }: { tripId: string; onClose: () => void }) {
  const { me, t } = useSession();
  const [what, setWhat] = useState("");
  const [where, setWhere] = useState("");
  const [canCall, setCanCall] = useState(true);
  const [phone, setPhone] = useState(me?.identity.phone ?? "");
  const [urgency, setUrgency] = useState<Urgency>("soon");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HelpResult | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = what.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.help(tripId, {
        what: text,
        ...(where.trim() ? { where: where.trim() } : {}),
        canCall,
        ...(canCall && phone.trim() ? { callbackPhone: phone.trim() } : {}),
        urgency,
      });
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError && err.code === "sign_in_required") setError(t("linkSessionNote"));
      else if (err instanceof TypeError) setError(t("offline"));
      else setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet sheet-help" role="dialog" aria-modal="true" aria-label={t("helpNow")} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2 className="assistant-title">
            <IconLifebuoy size={20} /> {t("helpNow")}
          </h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t("close")}>
            <IconX size={18} />
          </button>
        </div>

        {result ? (
          <div className="help-result stack">
            <p className="saved">
              <strong>{t("helpSent")}</strong>
            </p>
            {(result.assistantReply ?? result.emergency.assistantReply) && (
              <div className="bubble bubble-assistant">
                <span className="bubble-label">
                  <IconSparkles size={14} /> {t("assistant")}
                </span>
                <p className="bubble-body">{result.assistantReply ?? result.emergency.assistantReply}</p>
              </div>
            )}
            {result.emergency.assignedAgentName ? (
              <p className="small on-the-way">
                <span className="pulse" aria-hidden="true" /> {t("agentCalled", { agent: result.emergency.assignedAgentName })}
              </p>
            ) : (
              result.emergency.urgency === "urgent" && <p className="small">{t("lineAlerted")}</p>
            )}
            <a href={telLink(result.line)} className="button button-primary button-block help-line" rel="external">
              <IconPhoneCall size={20} />
              <span className="help-line-text">
                <span>{t("call24h")}</span>
                <strong>{result.line}</strong>
              </span>
            </a>
            <div className="row-buttons">
              <a href="/conversation" className="button button-small">
                <IconMessageCircle size={16} /> {t("viewInMessages")}
              </a>
              <button type="button" className="link-button" onClick={onClose}>
                {t("close")}
              </button>
            </div>
          </div>
        ) : (
          <form className="form" onSubmit={(e) => void submit(e)}>
            <p className="small">{t("helpNowBody")}</p>
            <label className="field">
              <span className="field-label">{t("helpWhat")}</span>
              <textarea value={what} onChange={(e) => setWhat(e.target.value)} placeholder={t("helpWhatPlaceholder")} rows={3} maxLength={2000} required autoFocus />
            </label>
            <label className="field">
              <span className="field-label">{t("helpWhere")}</span>
              <input type="text" value={where} onChange={(e) => setWhere(e.target.value)} placeholder={t("helpWherePlaceholder")} maxLength={300} />
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={canCall} onChange={(e) => setCanCall(e.target.checked)} />
              <span>{t("helpCanCall")}</span>
            </label>
            {canCall && (
              <label className="field">
                <span className="field-label">{t("helpPhone")}</span>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("phonePlaceholder")} maxLength={40} autoComplete="tel" />
                <span className="field-hint">
                  <IconPhone size={12} /> {t("helpPhoneHint")}
                </span>
              </label>
            )}
            <fieldset className="urgency">
              <legend className="field-label">{t("helpUrgency")}</legend>
              {URGENCIES.map((u) => (
                <label key={u} className={`urgency-option ${urgency === u ? "on" : ""}`}>
                  <input type="radio" name="urgency" value={u} checked={urgency === u} onChange={() => setUrgency(u)} />
                  <span className="list-text">
                    <strong>{t(URGENCY_KEYS[u]!)}</strong>
                    <span className="muted small">{t(URGENCY_HINTS[u])}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {error && <ErrorNote>{error}</ErrorNote>}
            <button type="submit" className="button button-primary button-block" disabled={busy || what.trim().length < 2}>
              {busy ? t("sending") : t("send")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
